import React, { useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Typography,
  Box,
  Alert,
  CircularProgress,
} from '@mui/material';
import { Send as SendIcon } from '@mui/icons-material';
import { api } from '../services/api-client';

interface RequestAccessDialogProps {
  open: boolean;
  onClose: () => void;
  currentRole: string;
  userEmail: string;
}

export default function RequestAccessDialog({
  open,
  onClose,
  currentRole,
  userEmail,
}: RequestAccessDialogProps) {
  const [requestedRole, setRequestedRole] = useState('developer');
  const [reason, setReason] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);

  const handleSubmit = async () => {
    if (!reason.trim()) {
      setError('Please provide a reason for your access request');
      return;
    }

    setLoading(true);
    setError('');

    try {
      await api.requestAccess(requestedRole, reason);
      setSuccess(true);
      setTimeout(() => {
        onClose();
        setSuccess(false);
        setReason('');
        setRequestedRole('developer');
      }, 3000);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to submit request');
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    if (!loading) {
      onClose();
      setError('');
      setSuccess(false);
      setReason('');
      setRequestedRole('developer');
    }
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
      <DialogTitle>Request Access</DialogTitle>
      <DialogContent>
        <Box sx={{ pt: 2 }}>
          {success ? (
            <Alert severity="success" sx={{ mb: 2 }}>
              Your access request has been submitted successfully! You will receive an email notification once it's approved.
            </Alert>
          ) : (
            <>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                Your current role is <strong>{currentRole}</strong>. Request elevated permissions to access additional features.
              </Typography>

              {error && (
                <Alert severity="error" sx={{ mb: 2 }}>
                  {error}
                </Alert>
              )}

              <FormControl fullWidth sx={{ mb: 3 }}>
                <InputLabel>Requested Role</InputLabel>
                <Select
                  value={requestedRole}
                  onChange={(e) => setRequestedRole(e.target.value)}
                  label="Requested Role"
                  disabled={loading}
                >
                  {currentRole !== 'developer' && (
                    <MenuItem value="developer">Developer</MenuItem>
                  )}
                  {currentRole !== 'admin' && (
                    <MenuItem value="admin">Admin</MenuItem>
                  )}
                </Select>
              </FormControl>

              <TextField
                fullWidth
                multiline
                rows={4}
                label="Reason for Request"
                placeholder="Please explain why you need elevated access..."
                value={reason}
                onChange={(e) => setReason(e.target.value)}
                disabled={loading}
                helperText={`Your request will be sent to jonathan@tripsby.ai for approval`}
              />

              <Typography variant="caption" color="text.secondary" sx={{ mt: 2, display: 'block' }}>
                Note: Access requests are logged and reviewed for security purposes.
              </Typography>
            </>
          )}
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose} disabled={loading}>
          {success ? 'Close' : 'Cancel'}
        </Button>
        {!success && (
          <Button
            onClick={handleSubmit}
            variant="contained"
            disabled={loading || !reason.trim()}
            startIcon={loading ? <CircularProgress size={20} /> : <SendIcon />}
          >
            {loading ? 'Submitting...' : 'Submit Request'}
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
}
